import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { Loader } from '../../../components';

const verdictColors = {
    'Safe': 'bg-green-100 text-green-800',
    'Tight': 'bg-yellow-100 text-yellow-800',
    'Risky': 'bg-red-100 text-red-800',
    'Acceptable': 'bg-green-100 text-green-800',
    'Review': 'bg-yellow-100 text-yellow-800',
    'Escalate': 'bg-orange-100 text-orange-800',
    'Decline': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const SectionHeader = ({ label, badge }) => (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</h3>
        {badge}
    </div>
);

const CheckRow = ({ label, name, value, options, onChange }) => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50/40 transition-colors">
        <span className="text-sm text-gray-700 w-3/5">{label}</span>
        <select
            name={name}
            value={value || ''}
            onChange={onChange}
            className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
            <option value="">—</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const defaultMonths = ['Month 1', 'Month 2', 'Month 3'];

const defaultExpenseCategories = [
    'Rent payments', 'Business activities', 'Buying airtime', 'Electricity bill',
    'Water bill', 'Insurance premiums', 'Savings / investment', 'Gambling',
    'Educational expenses', 'Family support / remittance', 'Building or land purchase',
    'Medical bills', 'Other personal projects'
].map(name => ({ name, month1: 0, month2: 0, month3: 0, avg: 0, pctOfIncome: 0 }));

function computeAvg(arr) {
    if (!arr || arr.length === 0) return 0;
    const nums = arr.filter(v => typeof v === 'number' && !isNaN(v));
    if (nums.length === 0) return 0;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function computeVolatility(arr) {
    if (!arr || arr.length < 2) return 0;
    const nums = arr.filter(v => typeof v === 'number' && !isNaN(v));
    if (nums.length < 2) return 0;
    const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
    if (mean === 0) return 0;
    const variance = nums.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / nums.length;
    return Math.round((Math.sqrt(variance) / mean) * 100) / 100;
}

function fmt(n) {
    return (n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(n) {
    return ((n || 0) * 100).toFixed(1) + '%';
}

function MoMoAnalysisIndex({ loanId, loanType }) {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Reference
    const [momoNumber, setMomoNumber] = useState('');
    const [momoProvider, setMomoProvider] = useState('');
    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [nameMatch, setNameMatch] = useState('');

    // Monthly data
    const [months, setMonths] = useState([...defaultMonths]);
    const [monthlyInflow, setMonthlyInflow] = useState([0, 0, 0]);
    const [monthlyOutflow, setMonthlyOutflow] = useState([0, 0, 0]);
    const [monthlyEndingBalance, setMonthlyEndingBalance] = useState([0, 0, 0]);
    const [monthlyTransactionCount, setMonthlyTransactionCount] = useState([0, 0, 0]);

    // Expenses & loans
    const [expenseCategories, setExpenseCategories] = useState([...defaultExpenseCategories]);
    const [loanPayments, setLoanPayments] = useState([]);

    // Standing order
    const [proposedRepayment, setProposedRepayment] = useState(0);
    const [monthsBelowRepayment, setMonthsBelowRepayment] = useState(0);

    // DTI decision
    const [useMomoIncomeForDTI, setUseMomoIncomeForDTI] = useState('');

    // Server-derived (read-only display)
    const [derived, setDerived] = useState({
        avgInflow: 0, avgOutflow: 0, avgEndingBalance: 0, incomeVolatility: 0,
        grossMonthlyIncome: 0, haircuts: {}, finalAdjustedIncome: 0, incomeVerdict: '',
        standingOrder: {}, totalActiveLoanPayments: 0, microCreditCount: 0, multipleMicroLenderFlag: ''
    });

    const fetchAnalysis = useCallback(async () => {
        try {
            const res = await fetchRequests(`/loans/staff/momo-analysis?loanId=${loanId}`, "GET", authStaff(accessToken), {}, {});
            if (res?.analysis) {
                const a = res.analysis;
                setMomoNumber(a.momoNumber || '');
                setMomoProvider(a.momoProvider || '');
                setPeriodFrom(a.statementPeriodFrom || '');
                setPeriodTo(a.statementPeriodTo || '');
                setNameMatch(a.nameMatchesGhanaCard || '');
                if (a.months?.length) setMonths(a.months);
                if (a.monthlyInflow?.length) setMonthlyInflow(a.monthlyInflow);
                if (a.monthlyOutflow?.length) setMonthlyOutflow(a.monthlyOutflow);
                if (a.monthlyEndingBalance?.length) setMonthlyEndingBalance(a.monthlyEndingBalance);
                if (a.monthlyTransactionCount?.length) setMonthlyTransactionCount(a.monthlyTransactionCount);
                if (a.expenseCategories?.length) setExpenseCategories(a.expenseCategories);
                if (a.loanPayments?.length) setLoanPayments(a.loanPayments);
                if (a.standingOrder) {
                    setProposedRepayment(a.standingOrder.proposedRepayment || 0);
                    setMonthsBelowRepayment(a.standingOrder.monthsBelowRepayment || 0);
                }
                setUseMomoIncomeForDTI(a.useMomoIncomeForDTI || '');
                setDerived({
                    avgInflow: a.avgInflow || 0,
                    avgOutflow: a.avgOutflow || 0,
                    avgEndingBalance: a.avgEndingBalance || 0,
                    incomeVolatility: a.incomeVolatility || 0,
                    grossMonthlyIncome: a.grossMonthlyIncome || 0,
                    haircuts: a.haircuts || {},
                    finalAdjustedIncome: a.finalAdjustedIncome || 0,
                    incomeVerdict: a.incomeVerdict || '',
                    standingOrder: a.standingOrder || {},
                    totalActiveLoanPayments: a.totalActiveLoanPayments || 0,
                    microCreditCount: a.microCreditCount || 0,
                    multipleMicroLenderFlag: a.multipleMicroLenderFlag || ''
                });
            }
        } catch (err) {
            // no existing analysis
        } finally {
            setLoading(false);
        }
    }, [loanId, accessToken]);

    useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

    // Live-compute local metrics for display
    const localAvgInflow = computeAvg(monthlyInflow);
    const localAvgOutflow = computeAvg(monthlyOutflow);
    const localVolatility = computeVolatility(monthlyInflow);

    const updateArrayCell = (setter) => (index, value) => {
        setter(prev => {
            const updated = [...prev];
            updated[index] = parseFloat(value) || 0;
            return updated;
        });
    };

    const updateExpenseCell = (index, field, value) => {
        setExpenseCategories(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
            return updated;
        });
    };

    const addLoanPayment = () => {
        setLoanPayments(prev => [...prev, { lender: '', month1: 0, month2: 0, month3: 0, avg: 0, type: '', status: '' }]);
    };

    const updateLoanPayment = (index, field, value) => {
        setLoanPayments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: field === 'lender' || field === 'type' || field === 'status' ? value : (parseFloat(value) || 0) };
            return updated;
        });
    };

    const removeLoanPayment = (index) => {
        setLoanPayments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetchRequests('/loans/staff/momo-analysis', 'POST', authStaff(accessToken), {}, {
                loanId, loanType, momoNumber, momoProvider,
                statementPeriodFrom: periodFrom, statementPeriodTo: periodTo,
                nameMatchesGhanaCard: nameMatch,
                months, monthlyInflow, monthlyOutflow, monthlyEndingBalance, monthlyTransactionCount,
                expenseCategories, loanPayments,
                standingOrder: { proposedRepayment, monthsBelowRepayment },
                useMomoIncomeForDTI
            });
            if (res?.err) {
                dispatch(show({ type: 'error', state: true, message: res.err }));
            } else if (res?.analysis) {
                const a = res.analysis;
                setDerived({
                    avgInflow: a.avgInflow || 0,
                    avgOutflow: a.avgOutflow || 0,
                    avgEndingBalance: a.avgEndingBalance || 0,
                    incomeVolatility: a.incomeVolatility || 0,
                    grossMonthlyIncome: a.grossMonthlyIncome || 0,
                    haircuts: a.haircuts || {},
                    finalAdjustedIncome: a.finalAdjustedIncome || 0,
                    incomeVerdict: a.incomeVerdict || '',
                    standingOrder: a.standingOrder || {},
                    totalActiveLoanPayments: a.totalActiveLoanPayments || 0,
                    microCreditCount: a.microCreditCount || 0,
                    multipleMicroLenderFlag: a.multipleMicroLenderFlag || ''
                });
                if (a.expenseCategories?.length) setExpenseCategories(a.expenseCategories);
                if (a.loanPayments?.length) setLoanPayments(a.loanPayments);
                dispatch(show({ type: 'success', state: true, message: 'MoMo analysis saved' }));
            }
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to save MoMo analysis' }));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <Loader color="text-gray-500" size="w-5 h-5" />
            </div>
        );
    }

    return (
        <div className="space-y-5 py-4">

            {/* Section A — Reference */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section A — MoMo Reference" />
                <div className="grid grid-cols-2 gap-4 p-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">MoMo Number</label>
                        <input type="text" value={momoNumber} onChange={e => setMomoNumber(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Provider</label>
                        <select value={momoProvider} onChange={e => setMomoProvider(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                            <option value="">—</option>
                            <option value="MTN">MTN</option>
                            <option value="Vodafone">Vodafone</option>
                            <option value="AirtelTigo">AirtelTigo</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Statement Period From</label>
                        <input type="text" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} placeholder="e.g. Feb 2026" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Statement Period To</label>
                        <input type="text" value={periodTo} onChange={e => setPeriodTo(e.target.value)} placeholder="e.g. Apr 2026" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                </div>
                <CheckRow label="Name on MoMo account matches Ghana Card?" name="nameMatch" value={nameMatch} options={['Yes', 'No']} onChange={e => setNameMatch(e.target.value)} />
            </div>

            {/* Section B — Monthly Inflow/Outflow */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section B — Monthly Income Summary" />
                <p className="px-4 py-2 text-xs text-gray-400">Enter monthly inflow, outflow, ending balance, and CASH_OUT transaction count</p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-gray-600 font-medium w-1/4">Metric</th>
                                {months.map((m, i) => (
                                    <th key={i} className="px-2 py-2.5 w-[16%]">
                                        <input type="text" value={m} onChange={e => { const u = [...months]; u[i] = e.target.value; setMonths(u); }}
                                            className="w-full text-sm text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 font-medium" />
                                    </th>
                                ))}
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[16%]">3-Mo Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { label: 'Total Inflow (GHS)', arr: monthlyInflow, setter: updateArrayCell(setMonthlyInflow) },
                                { label: 'Total Outflow (GHS)', arr: monthlyOutflow, setter: updateArrayCell(setMonthlyOutflow) },
                                { label: 'Ending Balance (GHS)', arr: monthlyEndingBalance, setter: updateArrayCell(setMonthlyEndingBalance), highlight: true },
                                { label: 'CASH_OUT Count', arr: monthlyTransactionCount, setter: updateArrayCell(setMonthlyTransactionCount) },
                            ].map(({ label, arr, setter, highlight }) => (
                                <tr key={label} className={`border-b border-gray-50 hover:bg-gray-50/40 ${highlight ? 'bg-blue-50/30' : ''}`}>
                                    <td className="px-4 py-2 text-gray-700">{label}</td>
                                    {[0, 1, 2].map(i => (
                                        <td key={i} className="px-2 py-1.5">
                                            <input type="number" value={arr[i] ?? 0} onChange={e => setter(i, e.target.value)} min={0} step="0.01"
                                                className="w-full text-sm text-right border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                                        </td>
                                    ))}
                                    <td className="px-2 py-2 text-right text-gray-500 font-medium tabular-nums">{fmt(computeAvg(arr))}</td>
                                </tr>
                            ))}
                            <tr className="border-b border-gray-50">
                                <td className="px-4 py-2 text-gray-700">Net Flow (Inflow - Outflow)</td>
                                {[0, 1, 2].map(i => (
                                    <td key={i} className="px-2 py-2 text-right text-gray-500 tabular-nums">{fmt((monthlyInflow[i] || 0) - (monthlyOutflow[i] || 0))}</td>
                                ))}
                                <td className="px-2 py-2 text-right text-gray-500 font-medium tabular-nums">{fmt(localAvgInflow - localAvgOutflow)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-6 text-sm">
                    <span className="text-gray-500">Income Volatility (CV): <strong className={localVolatility > 0.6 ? 'text-red-600' : localVolatility > 0.3 ? 'text-yellow-600' : 'text-gray-900'}>{localVolatility.toFixed(2)}</strong></span>
                </div>
            </div>

            {/* Section C — Expense Categories */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section C — Expense Categories" badge={<span className="text-xs text-gray-400">Enter monthly totals per category</span>} />
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-gray-600 font-medium w-1/4">Category</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[14%]">{months[0]}</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[14%]">{months[1]}</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[14%]">{months[2]}</th>
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[12%]">3-Mo Avg</th>
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[10%]">% Income</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenseCategories.map((cat, idx) => {
                                const avg = ((cat.month1 || 0) + (cat.month2 || 0) + (cat.month3 || 0)) / 3;
                                const pctIncome = derived.grossMonthlyIncome > 0 ? avg / derived.grossMonthlyIncome : 0;
                                return (
                                    <tr key={idx} className={`border-b border-gray-50 hover:bg-gray-50/40 ${pctIncome > 0.1 ? 'bg-red-50/30' : ''}`}>
                                        <td className="px-4 py-1.5 text-gray-700">{cat.name}</td>
                                        {['month1', 'month2', 'month3'].map(field => (
                                            <td key={field} className="px-2 py-1">
                                                <input type="number" value={cat[field] ?? 0} onChange={e => updateExpenseCell(idx, field, e.target.value)} min={0} step="0.01"
                                                    className="w-full text-sm text-right border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                                            </td>
                                        ))}
                                        <td className="px-2 py-1.5 text-right text-gray-500 tabular-nums">{fmt(avg)}</td>
                                        <td className={`px-2 py-1.5 text-right tabular-nums ${pctIncome > 0.1 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{pct(pctIncome)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Section D — Loan Payments */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section D — Loan Payments (from MoMo)"
                    badge={derived.multipleMicroLenderFlag === 'Yes'
                        ? <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Multiple micro-lenders</span>
                        : null}
                />
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-gray-600 font-medium">Lender</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[12%]">{months[0]}</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[12%]">{months[1]}</th>
                                <th className="px-2 py-2.5 text-gray-600 font-medium w-[12%]">{months[2]}</th>
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[10%]">Type</th>
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[10%]">Status</th>
                                <th className="px-2 py-2.5 w-[5%]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loanPayments.map((lp, idx) => (
                                <tr key={idx} className="border-b border-gray-50">
                                    <td className="px-4 py-1.5">
                                        <input type="text" value={lp.lender || ''} onChange={e => updateLoanPayment(idx, 'lender', e.target.value)} placeholder="Lender name"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                                    </td>
                                    {['month1', 'month2', 'month3'].map(field => (
                                        <td key={field} className="px-2 py-1">
                                            <input type="number" value={lp[field] ?? 0} onChange={e => updateLoanPayment(idx, field, e.target.value)} min={0} step="0.01"
                                                className="w-full text-sm text-right border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                                        </td>
                                    ))}
                                    <td className="px-2 py-1">
                                        <select value={lp.type || ''} onChange={e => updateLoanPayment(idx, 'type', e.target.value)}
                                            className="w-full text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                                            <option value="">—</option>
                                            <option value="formal">Formal</option>
                                            <option value="micro-credit">Micro-credit</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-1">
                                        <select value={lp.status || ''} onChange={e => updateLoanPayment(idx, 'status', e.target.value)}
                                            className="w-full text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                                            <option value="">—</option>
                                            <option value="active">Active</option>
                                            <option value="closed">Closed</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-1">
                                        <button type="button" onClick={() => removeLoanPayment(idx)} className="text-red-400 hover:text-red-600 text-xs">x</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 border-t border-gray-100">
                    <button type="button" onClick={addLoanPayment} className="text-sm text-blue-600 hover:text-blue-800 font-medium">+ Add loan payment</button>
                </div>
            </div>

            {/* Section E — Haircut Breakdown (auto-derived, shown after save) */}
            {derived.grossMonthlyIncome > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <SectionHeader label="Section E — Income & Haircut Adjustments"
                        badge={derived.incomeVerdict ? <VerdictBadge verdict={derived.incomeVerdict} /> : null} />
                    <div className="divide-y divide-gray-100">
                        <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-700">Gross Monthly Income (GHS)</span>
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmt(derived.grossMonthlyIncome)}</span>
                        </div>
                        {[
                            { label: 'DTI haircut', key: 'dtiHaircut' },
                            { label: 'Gambling haircut', key: 'gamblingHaircut' },
                            { label: 'Volatility haircut', key: 'volatilityHaircut' },
                            { label: 'Withdrawal frequency haircut', key: 'withdrawalHaircut' },
                            { label: 'Micro-credit haircut', key: 'microCreditHaircut' },
                            { label: 'Expense category haircut', key: 'expenseCategoryHaircut' },
                        ].map(({ label, key }) => (
                            <div key={key} className="flex items-center justify-between px-4 py-2.5">
                                <span className="text-sm text-gray-600">{label}</span>
                                <span className={`text-sm tabular-nums ${(derived.haircuts[key] || 0) > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                                    {pct(derived.haircuts[key] || 0)}
                                </span>
                            </div>
                        ))}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                            <span className="text-sm font-medium text-gray-700">Combined Haircut (capped at 100%)</span>
                            <span className={`text-sm font-semibold tabular-nums ${(derived.haircuts.combinedHaircut || 0) >= 0.5 ? 'text-red-600' : 'text-gray-900'}`}>
                                {pct(derived.haircuts.combinedHaircut || 0)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 bg-green-50/50">
                            <span className="text-sm font-semibold text-gray-800">Final Adjusted Income (GHS)</span>
                            <span className="text-sm font-bold text-gray-900 tabular-nums">{fmt(derived.finalAdjustedIncome)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Section F — Standing Order */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section F — Standing Order (MoMo)"
                    badge={derived.standingOrder?.verdict ? <VerdictBadge verdict={derived.standingOrder.verdict} /> : null} />
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700 w-3/5">Proposed monthly repayment (GHS)</span>
                    <input type="number" value={proposedRepayment} onChange={e => setProposedRepayment(parseFloat(e.target.value) || 0)} min={0} step="0.01"
                        className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Avg ending balance (GHS)</span>
                    <span className="text-sm font-medium text-gray-900 tabular-nums">{fmt(computeAvg(monthlyEndingBalance))}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Min monthly balance (GHS)</span>
                    <span className="text-sm font-medium text-gray-900 tabular-nums">
                        {fmt(monthlyEndingBalance.filter(v => typeof v === 'number').length ? Math.min(...monthlyEndingBalance.filter(v => typeof v === 'number')) : 0)}
                    </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700 w-3/5">Months where balance &lt; repayment</span>
                    <input type="number" value={monthsBelowRepayment} onChange={e => setMonthsBelowRepayment(parseInt(e.target.value) || 0)} min={0}
                        className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
            </div>

            {/* Section G — DTI Decision */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section G — Reliability Decision" />
                <CheckRow label="Use MoMo income for DTI calculation?" name="useMomoIncomeForDTI" value={useMomoIncomeForDTI} options={['Yes', 'No']} onChange={e => setUseMomoIncomeForDTI(e.target.value)} />
                <p className="px-4 py-2 text-xs text-gray-400">If "Yes", the Final Adjusted Income above is added to bank income for DTI in Phase 4</p>
            </div>

            {/* Save Button */}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] transition-colors disabled:opacity-50"
                >
                    {saving && <Loader color="text-white" size="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save MoMo Analysis'}
                </button>
                {derived.incomeVerdict && (
                    <span className="text-sm text-gray-500">
                        Income: <VerdictBadge verdict={derived.incomeVerdict} />
                    </span>
                )}
                {derived.standingOrder?.verdict && (
                    <span className="text-sm text-gray-500 ml-2">
                        Standing Order: <VerdictBadge verdict={derived.standingOrder.verdict} />
                    </span>
                )}
            </div>
        </div>
    );
}

export default MoMoAnalysisIndex;
